/** A visual viewport this much shorter than the window means an on-screen keyboard is open (pinch zoom is off). */
const KEYBOARD_MIN = 120;

/** Room for iOS 26's form toolbar (arrows and Done), which floats above the keyboard, over the page. */
export const FLOATING_TOOLBAR = 52;

export type KeyboardLayout =
  | { open: false }
  | { open: true; top: number; height: number; bottom: number; toolbar: number };

/**
 * Where the visible part of the page is while a keyboard is open: `top` and `height` of the visual
 * viewport, and `bottom`, how far its end is above the window's (what fixed sheets move up by).
 */
export function keyboardLayout(windowHeight: number, viewport: { height: number; offsetTop: number }, userAgent: string): KeyboardLayout {
  if (windowHeight - viewport.height < KEYBOARD_MIN) return { open: false };
  return {
    open: true,
    top: Math.max(0, Math.round(viewport.offsetTop)),
    height: Math.round(viewport.height),
    bottom: Math.max(0, Math.round(windowHeight - viewport.offsetTop - viewport.height)),
    toolbar: hasFloatingToolbar(userAgent) ? FLOATING_TOOLBAR : 0,
  };
}

/**
 * iOS 26 floats the form toolbar over the page; earlier versions dock it to the keyboard. Safari
 * names its own version; other iOS browsers report the OS, which iOS 26 freezes at 18_6.
 */
export function hasFloatingToolbar(userAgent: string): boolean {
  // iPadOS Safari says Macintosh; a Mac never opens an on-screen keyboard, so its answer is never used.
  if (!/\b(iPhone|iPad|iPod|Macintosh)\b/.test(userAgent)) return false;
  const safari = /\bVersion\/(\d+)/.exec(userAgent);
  if (safari) return Number(safari[1]) >= 26;
  const os = /\bOS (\d+)_(\d+)/.exec(userAgent);
  if (!os) return true;
  const [major, minor] = [Number(os[1]), Number(os[2])];
  return major >= 26 || (major === 18 && minor >= 6);
}
